import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';
import VideoPlayer from '@/components/VideoPlayer';
import { FaArrowLeft, FaDumbbell, FaClock, FaDownload, FaFire, FaCalendarAlt, FaPlay, FaTimes } from 'react-icons/fa';
import { toast } from 'sonner';
import CardioRecommendedCard from '@/components/CardioRecommendedCard';
import ContinuationHistoryCard from '@/components/ContinuationHistoryCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 2026-02 · Botón "¿No puedes hacerlo? Ver alternativas" en Fit Evolution.
// Solo se renderiza si el ejercicio trae `alternatives: [...]` (hidratadas por
// el backend desde `replacement_video_ids` definidos por admin en el gestor
// de videos). Al elegir una alternativa llama al endpoint `swap-exercise`
// que persiste el cambio en la rutina del usuario.
const AlternativeExerciseSwap = ({ exercise, exIndex, dayIndex, onSwapped }) => {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const alts = (exercise?.alternatives || []).filter(Boolean);
  if (alts.length === 0) return null;
  const chooseAlternative = async (altName) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/user/workout-plan/swap-exercise`,
        {
          exercise_index: exIndex,
          new_exercise_name: altName,
          day_index: typeof dayIndex === 'number' ? dayIndex : null,
        },
        { headers }
      );
      toast.success(`Ejercicio cambiado a "${altName}"`);
      onSwapped?.(altName);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reemplazar el ejercicio');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="relative inline-block" data-testid={`alt-swap-wrap-${dayIndex}-${exIndex}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide hover:scale-105 transition-transform cursor-pointer"
        style={{
          background: 'rgba(236, 72, 153, 0.18)',
          color: '#F472B6',
          border: '1px solid rgba(236, 72, 153, 0.35)',
        }}
        title="¿No puedes hacerlo? Ver alternativas"
        data-testid={`alt-swap-toggle-${dayIndex}-${exIndex}`}
      >
        🔁 Alternativas ({alts.length})
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 z-30 w-max max-w-xs rounded-lg p-2 shadow-xl"
          style={{
            background: 'rgba(17, 17, 27, 0.98)',
            border: '1px solid rgba(236, 72, 153, 0.5)',
          }}
          data-testid={`alt-swap-panel-${dayIndex}-${exIndex}`}
        >
          <p className="text-[10px] text-pink-300 mb-2 uppercase tracking-wider font-semibold px-1">
            Elige un reemplazo
          </p>
          <div className="space-y-1">
            {alts.map((a, i) => (
              <button
                key={i}
                type="button"
                disabled={saving}
                onClick={() => chooseAlternative(a)}
                className="w-full text-left px-3 py-2 text-sm rounded bg-black/40 hover:bg-pink-500/20 text-white disabled:opacity-50 transition-colors whitespace-normal"
                data-testid={`alt-swap-option-${dayIndex}-${exIndex}-${i}`}
              >
                → {a}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper para obtener embed URL de un video (YouTube / Drive / directo)
// ═════ ESTE HELPER ES IDÉNTICO al de Videos.jsx para garantizar que un video
// que reproduce en la biblioteca TAMBIÉN reproduce en la rutina. ═════
const getEmbedUrl = (url, source) => {
  if (!url) return null;

  // YouTube
  if (source === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = '';
    if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    }
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
  }

  // Google Drive - formato preview
  if (source === 'drive' || url.includes('drive.google.com')) {
    let fileId = '';
    if (url.includes('/file/d/')) {
      fileId = url.split('/file/d/')[1]?.split('/')[0]?.split('?')[0];
    } else if (url.includes('id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    }
    if (fileId) {
      return `https://drive.google.com/file/d/${fileId}/preview`;
    }
  }

  return url;
};

// Verificar si necesita iframe (IDÉNTICO a Videos.jsx)
const needsIframe = (url, source) => {
  return source === 'youtube' || source === 'drive' ||
         url?.includes('youtube.com') || url?.includes('youtu.be') ||
         url?.includes('drive.google.com');
};

// Extrae el file_id de una URL de Google Drive
const getDriveFileId = (url) => {
  if (!url || !url.includes('drive.google.com')) return null;
  if (url.includes('/file/d/')) return url.split('/file/d/')[1]?.split('/')[0]?.split('?')[0] || null;
  if (url.includes('id=')) return url.split('id=')[1]?.split('&')[0] || null;
  return null;
};

// Detecta URLs de Cloudinary (reproducción HTML5 nativa, sin proxy ni iframe).
const isCloudinaryUrl = (url) => {
  if (!url) return false;
  return /res\.cloudinary\.com\/.+\/video\/upload\//i.test(url);
};

// Detecta URLs de Sirv (reproducción HTML5 nativa).
const isSirvUrl = (url) => {
  if (!url) return false;
  return /\.sirv\.com\//i.test(url);
};

// 2026-02 · Bunny Stream / Bunny CDN
const isBunnyPlayerUrl = (url) => !!url && /(player|iframe)\.mediadelivery\.net\/(play|embed)\//i.test(url);
const isBunnyDirectUrl = (url) => !!url && /\.b-cdn\.net\//i.test(url);

// Detecta iPhone / iPad / iPod (Safari incluido). En iOS el iframe de Drive
// suele reproducir SOLO audio; usamos <video> HTML5 con URL directa de Drive.
const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ reporta 'MacIntel' pero con touch → también contar como iOS
  const isIPadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  return isIOS || isIPadOS;
};

// Mapeo de equipos para mostrar nombres legibles
const EQUIPMENT_LABELS = {
  'libre': 'Peso Corporal',
  'mancuerna': 'Mancuerna',
  'barra': 'Barra',
  'maquina': 'Máquina',
  'polea': 'Polea / Cable',
  'kettlebell': 'Kettlebell',
  'banda': 'Banda Elástica',
  'smith': 'Máquina Smith',
  'trx': 'TRX / Suspensión',
  'otro': 'Otro'
};

const MyWorkout = ({ user }) => {
  const navigate = useNavigate();
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState(null);
  const [videoModal, setVideoModal] = useState(null); // { url, name }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Obtener el token de autenticación y email del usuario
      const token = localStorage.getItem('deisycfit_token');
      const savedUser = localStorage.getItem('deisycfit_user');
      let userEmail = null;
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        userEmail = userData.email;
      }
      
      // Intentar obtener el plan activo CON autenticación
      try {
        const response = await axios.get(`${API}/workout/active/current`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        // Si hay datos del plan, mostrarlo
        if (response.data && (response.data.days || response.data.workout_days || response.data.training_days || response.data.id)) {
          setWorkoutPlan(response.data);
        } else if (response.data?.fit_evolution_locked) {
          setWorkoutPlan(response.data);
        } else if (response.data?.pending_release) {
          // El plan está en preparación
          setFormStatus({ status: 'pending', form_completed: true, message: response.data.message });
        } else {
          // No hay plan, verificar estado del formulario
          if (userEmail) {
            const statusResponse = await axios.get(`${API}/personalized-plans/check-status/${encodeURIComponent(userEmail)}`);
            console.log('Workout form status:', statusResponse.data);
            setFormStatus(statusResponse.data);
          }
        }
      } catch (error) {
        // Si falla la autenticación (401), intentar con el endpoint por email
        if (error.response?.status === 401 && userEmail) {
          try {
            const response = await axios.get(`${API}/workout/active/by-email/${encodeURIComponent(userEmail)}`);
            if (response.data && (response.data.days || response.data.workout_days || response.data.training_days || response.data.id)) {
              setWorkoutPlan(response.data);
              return;
            } else if (response.data?.fit_evolution_locked) {
              setWorkoutPlan(response.data);
              return;
            } else if (response.data?.pending_release) {
              setFormStatus({ status: 'pending', form_completed: true, message: response.data.message });
              return;
            }
          } catch (e) {
            console.error('Error with fallback endpoint:', e);
          }
        }
        
        // Si no hay plan activo, SIEMPRE verificar estado del formulario
        if (userEmail) {
          try {
            const statusResponse = await axios.get(`${API}/personalized-plans/check-status/${encodeURIComponent(userEmail)}`);
            console.log('Workout form status (after error):', statusResponse.data);
            setFormStatus(statusResponse.data);
          } catch (e) {
            console.error('Error checking form status:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // Verificar que el plan tiene ID válido
      const planId = workoutPlan?.id;
      if (!planId) {
        toast.error('No se puede descargar: el plan no tiene un identificador válido');
        return;
      }
      
      toast.info('Generando PDF...');
      
      // Obtener token para autenticación
      const token = localStorage.getItem('deisycfit_token');
      
      const response = await axios.get(`${API}/download/workout-pdf/${planId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rutina_${workoutPlan.workout_name?.replace(/\s+/g, '_') || 'entrenamiento'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('¡PDF descargado exitosamente! 📄');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      if (error.response?.status === 404) {
        toast.error('Rutina no encontrada. Por favor contacta a soporte.');
      } else {
        toast.error('Error al descargar el PDF. Intenta de nuevo.');
      }
    }
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        <div className="text-center">
          <div 
            className="w-16 h-16 rounded-full mx-auto mb-4 animate-pulse"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
          />
          <p className="text-white text-lg">Cargando tu rutina...</p>
        </div>
      </div>
    );
  }

  // Si el formulario ya fue completado pero no hay rutina aún - mostrar "En Preparación"
  // Nota: formStatus.status puede ser 'sent' si la dieta ya fue enviada, pero la rutina aún no
  // Por eso también verificamos si status es 'sent' pero no hay workoutPlan
  if (!workoutPlan && formStatus?.form_completed === true && (formStatus?.status === 'pending' || formStatus?.status === 'preparing' || formStatus?.status === 'sent')) {
    return (
      <div 
        className="min-h-screen py-12 px-6 relative"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 70% 30%, rgba(0, 240, 255, 0.15), transparent 50%)'
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6 text-gray-400 hover:text-white">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          
          <div 
            className="rounded-2xl p-12 text-center backdrop-blur-xl"
            style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <div 
              className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8"
              style={{ 
                background: 'linear-gradient(135deg, #00F0FF, #0088FF)',
                boxShadow: '0 0 40px rgba(0, 240, 255, 0.4)'
              }}
            >
              <FaClock className="text-4xl text-white animate-pulse" />
            </div>
            
            <h1 
              className="text-3xl md:text-4xl font-bold mb-4 text-white uppercase"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Tu Rutina Está en
              <br />
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))' }}
              >
                Preparación
              </span>
            </h1>
            
            <p className="text-gray-400 mb-8 text-lg max-w-md mx-auto">
              Deisy está preparando tu rutina de entrenamiento personalizada.
              Te notificaremos por email cuando esté lista.
            </p>
            
            <div 
              className="rounded-xl p-6 max-w-md mx-auto"
              style={{ 
                background: 'rgba(0, 240, 255, 0.1)',
                border: '1px solid rgba(0, 240, 255, 0.3)'
              }}
            >
              <p className="text-sm text-gray-300">
                <span className="text-cyan-400 font-semibold">⏱️ Tiempo estimado:</span>
                <br />
                <span className="text-white">Máximo 48 horas</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!workoutPlan) {
    // No hay rutina ni formulario completado - mostrar opción de llenar formulario
    return (
      <div 
        className="min-h-screen py-12 px-6 relative"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        {/* Mesh gradient background */}
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 70% 30%, rgba(0, 240, 255, 0.15), transparent 50%), radial-gradient(circle at 30% 70%, rgba(var(--brand-primary-glow), 0.1), transparent 40%)'
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')} 
            className="mb-8 text-gray-400 hover:text-white absolute top-0 left-0"
          >
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="pt-20"
          >
            <div 
              className="inline-flex items-center justify-center w-28 h-28 rounded-full mb-8"
              style={{ 
                background: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))',
                boxShadow: '0 0 60px rgba(0, 240, 255, 0.4)'
              }}
            >
              <FaDumbbell className="text-5xl text-white" />
            </div>
            
            <h1 
              className="text-4xl md:text-5xl font-bold mb-4 text-white uppercase tracking-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Mi Rutina de
              <br />
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))' }}
              >
                Entrenamiento
              </span>
            </h1>
            
            <p className="text-gray-400 mb-8 text-lg max-w-xl mx-auto">
              Completa el formulario para que Deisy pueda crear tu rutina de entrenamiento personalizada
            </p>
            
            <div 
              className="rounded-2xl p-8 mb-8 max-w-xl mx-auto backdrop-blur-xl"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <p 
                className="text-2xl font-bold mb-2"
                style={{ color: '#00F0FF' }}
              >
                📝 Primer paso
              </p>
              <p className="text-gray-400">Llena tus datos para recibir una rutina 100% personalizada</p>
            </div>
            
            <Button 
              onClick={() => navigate('/personalized-form')} 
              className="rounded-full px-8 py-6 font-bold uppercase tracking-wider text-white"
              style={{ 
                background: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))',
                boxShadow: '0 0 30px rgba(0, 240, 255, 0.4)'
              }}
            >
              Llenar Formulario
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Si la rutina está pendiente
  if (workoutPlan.pending_release || workoutPlan.is_released === false) {
    return (
      <div 
        className="min-h-screen py-12 px-6 relative"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 70% 30%, rgba(0, 240, 255, 0.15), transparent 50%)'
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6 text-gray-400 hover:text-white">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          
          <div 
            className="rounded-2xl p-12 text-center backdrop-blur-xl"
            style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <div 
              className="inline-flex items-center justify-center w-28 h-28 rounded-full mb-8"
              style={{ 
                background: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))',
                boxShadow: '0 0 40px rgba(0, 240, 255, 0.4)'
              }}
            >
              <FaClock className="text-5xl text-white" />
            </div>
            <h1 
              className="text-4xl font-bold mb-6 text-white uppercase"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              ¡Estamos preparando tu rutina!
            </h1>
            <div className="mb-8">
              <p className="text-xl text-gray-300 mb-4">
                {workoutPlan.message || "Tu rutina de entrenamiento está siendo preparada por Deisy."}
              </p>
              {workoutPlan.fit_evolution_locked ? (
                <Button onClick={() => navigate('/my-progress')} className="rounded-full px-8 py-3 font-bold text-black mt-2"
                  style={{ background: '#facc15' }} data-testid="workout-locked-upload-photos-btn">
                  Adjuntar mis fotos iniciales
                </Button>
              ) : (
                <p className="text-2xl font-bold" style={{ color: '#00F0FF' }}>⏰ En un plazo máximo de 48 horas estará lista</p>
              )}
            </div>
            <Button 
              onClick={() => navigate('/dashboard')} 
              className="rounded-full px-8 py-4 font-bold uppercase text-white"
              style={{ background: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))' }}
            >
              Volver al Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Obtener días de workout (soportar todas las estructuras posibles)
  const workoutDays = [workoutPlan.days, workoutPlan.workout_days, workoutPlan.training_days]
    .find((d) => Array.isArray(d) && d.length > 0) || [];

  return (
    <div 
      className="min-h-screen py-8 px-4 md:px-6 relative"
      style={{ backgroundColor: '#0a0a0a' }}
    >
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.1), transparent 50%), radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.08), transparent 40%)'
        }}
      />
      
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')} 
            className="text-gray-400 hover:text-white"
          >
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          {workoutPlan.id && (
            <Button 
              onClick={handleDownloadPDF} 
              className="rounded-full px-6 font-bold uppercase tracking-wider text-white"
              style={{ background: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))' }}
            >
              <FaDownload className="mr-2" /> Descargar PDF
            </Button>
          )}
        </div>

        {/* Título */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <p 
            className="text-xs font-bold uppercase tracking-[0.3em] mb-3"
            style={{ color: '#00F0FF' }}
          >
            Tu rutina de entrenamiento
          </p>
          <h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-tight"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {workoutPlan.workout_name || 'Mi Rutina'}
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span 
              className="px-4 py-2 rounded-full text-sm font-bold uppercase"
              style={{ background: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))', color: 'white' }}
            >
              {workoutDays.length} días/semana
            </span>
            {workoutPlan.is_personalized_plan && (
              <span 
                className="px-4 py-2 rounded-full text-sm font-bold uppercase"
                style={{ background: 'rgba(var(--brand-primary-glow), 0.2)', color: 'var(--brand-primary)', border: '1px solid rgba(var(--brand-primary-glow), 0.3)' }}
              >
                💪 Personalizado
              </span>
            )}
          </div>
        </motion.div>

        {/* Cardio Section — Fase 3 Fit Evolution */}
        <CardioRecommendedCard workoutPlan={workoutPlan} />

        {/* Timeline de continuaciones mensuales — motivador */}
        <ContinuationHistoryCard email={(() => { try { return JSON.parse(localStorage.getItem('deisycfit_user') || '{}').email; } catch (_e) { return null; } })()} />

        {/* Workout Days */}
        {workoutDays.length > 0 ? (
          <div className="space-y-8">
            {workoutDays.map((day, dayIndex) => (
              <motion.div
                key={dayIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: dayIndex * 0.1 }}
              >
                {day.is_rest_day ? (
                  /* 2026-02 · Día de descanso — tarjeta diferenciada */
                  <div
                    className="rounded-2xl p-6 backdrop-blur-xl"
                    style={{
                      background: 'linear-gradient(135deg, rgba(71,85,105,0.18), rgba(15,23,42,0.55))',
                      border: '1px dashed rgba(148,163,184,0.45)',
                    }}
                    data-testid={`rest-day-card-${dayIndex}`}
                  >
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ background: 'linear-gradient(135deg, rgba(148,163,184,0.35), rgba(71,85,105,0.4))' }}
                      >
                        🌙
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4
                            className="text-2xl font-bold text-white uppercase"
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                          >
                            {day.day_name || day.name || `Día ${dayIndex + 1}`}
                          </h4>
                          <span
                            className="text-[11px] px-2 py-1 rounded-full font-semibold tracking-wide uppercase"
                            style={{ background: 'rgba(148,163,184,0.25)', color: '#e2e8f0', border: '1px solid rgba(148,163,184,0.45)' }}
                            data-testid={`rest-day-badge-${dayIndex}`}
                          >
                            Día de descanso
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 italic">
                          Hoy toca recuperar. Sin sesión de fuerza programada.
                        </p>
                      </div>
                    </div>

                    {day.rest_notes && String(day.rest_notes).trim() ? (
                      <div
                        className="rounded-xl p-4"
                        style={{ background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.25)' }}
                        data-testid={`rest-day-notes-${dayIndex}`}
                      >
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                          📝 Indicaciones de tu coach
                        </p>
                        <p className="text-slate-100 text-sm whitespace-pre-line leading-relaxed">
                          {day.rest_notes}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 italic text-center py-4">
                        💤 Aprovecha para hidratarte, dormir bien y volver con todo mañana.
                      </p>
                    )}
                  </div>
                ) : (
                <div 
                  className="rounded-2xl p-6 backdrop-blur-xl"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {/* Day Header */}
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))' }}
                    >
                      <FaCalendarAlt className="text-white text-lg" />
                    </div>
                    <div>
                      <h4 
                        className="text-2xl font-bold text-white uppercase"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {day.day_name || day.name || `Día ${dayIndex + 1}`}
                      </h4>
                      <div className="flex gap-2 mt-1">
                        {day.primary_muscle && (
                          <span 
                            className="text-xs px-2 py-1 rounded-full"
                            style={{ background: 'rgba(var(--brand-primary-glow), 0.2)', color: 'var(--brand-primary)' }}
                          >
                            {day.primary_muscle}
                          </span>
                        )}
                        {day.secondary_muscle && day.secondary_muscle !== 'none' && (
                          <span 
                            className="text-xs px-2 py-1 rounded-full"
                            style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}
                          >
                            {day.secondary_muscle}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Exercises */}
                  <div className="space-y-4">
                    {(day.exercises || []).length > 0 ? (
                      day.exercises.map((exercise, exIndex) => {
                        // Convertir series_detail a formato series (array de objetos).
                        // Fit Evolution v2 (motor determinístico) emite ARRAY DE OBJETOS:
                        //   {set, reps, rest, load, method, method_name, method_description}
                        // Legacy: array de strings tipo '3x12'.
                        let series = Array.isArray(exercise.series) ? exercise.series : [];
                        if (series.length === 0 && exercise.series_detail && Array.isArray(exercise.series_detail)) {
                          // 1) Nueva forma: array de objetos → mapear directo preservando method
                          if (exercise.series_detail.length > 0 && exercise.series_detail[0] && typeof exercise.series_detail[0] === 'object') {
                            series = exercise.series_detail.map((d) => ({
                              reps: d.reps || '12',
                              rest: d.rest || '60',
                              load: d.load || '',
                              method: d.method || 'normal',
                              method_name: d.method_name || '',
                              method_description: d.method_description || '',
                            }));
                          } else {
                            // 2) Legacy: parsear strings tipo '3x12' / '1x15'
                            series = exercise.series_detail.map((detail) => {
                              const match = String(detail).match(/(\d+)\s*x\s*(\d+)/i);
                              if (match) {
                                const numSeries = parseInt(match[1]);
                                const reps = match[2];
                                if (numSeries > 1) {
                                  return Array(numSeries).fill(null).map(() => ({
                                    reps: reps,
                                    rest: '60',
                                    method: 'normal'
                                  }));
                                }
                                return { reps: reps, rest: '60', method: 'normal' };
                              }
                              const repsOnly = String(detail).match(/(\d+)/);
                              return { reps: repsOnly ? repsOnly[1] : '12', rest: '60', method: 'normal' };
                            }).flat();
                          }
                        }
                        
                        // Si aún no hay series pero hay datos del ejercicio, crear una serie por defecto
                        if (series.length === 0 && (exercise.sets || exercise.reps || (typeof exercise.series === 'string' && exercise.series))) {
                          const numSets = parseInt(exercise.sets) || parseInt(exercise.series) || 3;
                          const reps = exercise.reps || '12';
                          series = Array(numSets).fill(null).map(() => ({
                            reps: reps,
                            rest: exercise.rest || '60',
                            method: exercise.method || 'normal'
                          }));
                        }
                        
                        const methodLabels = {
                          'normal': 'Normal',
                          'dropset': 'Dropset',
                          'rest_pause': 'Rest-Pause',
                          'cluster': 'Cluster Sets',
                          'myo_reps': 'Myo-Reps',
                          'tempo_controlado': 'Tempo Controlado',
                          'pausas_isometricas': 'Pausas Isométricas',
                          'excentrica_lenta': 'Excéntrica Lenta',
                          'back_off_set': 'Back-Off Set',
                          'super_serie': 'Super Serie',
                          'amrap': 'AMRAP',
                          'emom': 'EMOM'
                        };
                        
                        // Explicaciones completas de cada método
                        const methodDescriptions = {
                          'dropset': {
                            description: 'Reducción progresiva del peso sin descanso entre series.',
                            example: 'Ej: 10 reps con 20kg → Sin descanso → 10 reps con 15kg → Sin descanso → 10 reps con 10kg'
                          },
                          'rest_pause': {
                            description: 'Realizar repeticiones hasta el fallo, descansar brevemente y continuar.',
                            example: 'Ej: 8 reps → 15 seg descanso → 3-4 reps → 15 seg descanso → 2-3 reps'
                          },
                          'cluster': {
                            description: 'Series divididas en mini-sets con descansos cortos intra-serie.',
                            example: 'Ej: 2 reps → 20 seg → 2 reps → 20 seg → 2 reps (total 6 reps con peso pesado)'
                          },
                          'myo_reps': {
                            description: 'Serie de activación seguida de mini-series con descansos muy cortos.',
                            example: 'Ej: 15 reps (activación) → 5 seg → 5 reps → 5 seg → 5 reps → 5 seg → 5 reps'
                          },
                          'tempo_controlado': {
                            description: 'Control del tiempo en cada fase del movimiento (excéntrica-pausa-concéntrica).',
                            example: 'Ej: 3-1-1 = 3 seg bajando, 1 seg pausa, 1 seg subiendo'
                          },
                          'pausas_isometricas': {
                            description: 'Pausas en puntos específicos del recorrido para aumentar tensión.',
                            example: 'Ej: Mantener posición en el punto de máxima contracción por 2-3 segundos'
                          },
                          'excentrica_lenta': {
                            description: 'Fase excéntrica (bajar el peso) muy controlada y lenta.',
                            example: 'Ej: Bajar el peso en 4-6 segundos, subir en 1-2 segundos'
                          },
                          'back_off_set': {
                            description: 'Serie final con peso reducido para más volumen después de series pesadas.',
                            example: 'Ej: Después de 3x5 pesado → 1x12-15 con 60% del peso'
                          },
                          'super_serie': {
                            description: 'Dos ejercicios seguidos sin descanso, generalmente antagonistas.',
                            example: 'Ej: Curl bíceps → Sin descanso → Extensión tríceps'
                          },
                          'amrap': {
                            description: 'As Many Reps As Possible - Hacer todas las reps posibles.',
                            example: 'Ej: Con un peso moderado, realizar reps hasta el fallo técnico'
                          },
                          'emom': {
                            description: 'Every Minute On the Minute - Realizar reps al inicio de cada minuto.',
                            example: 'Ej: Cada minuto por 10 min: 5 dominadas, descansar el resto del minuto'
                          }
                        };
                        
                        const restLabels = {
                          '30': '30 seg', '45': '45 seg', '60': '1 min', '75': '1:15',
                          '90': '1:30', '120': '2 min', '150': '2:30', '180': '3 min',
                          '240': '4 min', '300': '5 min'
                        };
                        
                        // Encontrar métodos especiales usados en este ejercicio
                        const specialMethodsUsed = [...new Set(
                          series
                            .filter(s => s.method && s.method !== 'normal' && methodDescriptions[s.method])
                            .map(s => s.method)
                        )];
                        
                        return (
                          <div key={exIndex} className="relative">
                            {/* Badge de superserie (bi/tri/superserie/circuito) */}
                            {(() => {
                              const exs = day.exercises || [];
                              const prevLinked = exIndex > 0 && exs[exIndex - 1]?.link_next;
                              const isGroupFirst = !prevLinked && exercise.link_next;
                              if (!isGroupFirst) return null;
                              let end = exIndex;
                              while (end < exs.length - 1 && exs[end]?.link_next) end++;
                              const size = end - exIndex + 1;
                              let gcount = 0;
                              for (let k = 0; k < exIndex; k++) {
                                if (exs[k]?.link_next && (k === 0 || !exs[k - 1]?.link_next)) gcount++;
                              }
                              const letter = String.fromCharCode(65 + gcount);
                              const label = size === 2 ? 'BISERIE' : size === 3 ? 'TRISERIE' : size >= 5 ? 'CIRCUITO' : 'SUPERSERIE';
                              const bg = label === 'BISERIE' ? 'linear-gradient(90deg,#a855f7,#d946ef)'
                                : label === 'TRISERIE' ? 'linear-gradient(90deg,#f97316,#ef4444)'
                                : label === 'CIRCUITO' ? 'linear-gradient(90deg,#10b981,#14b8a6)'
                                : 'linear-gradient(90deg,#ec4899,#f43f5e)';
                              return (
                                <div className="mb-2 px-3 py-1.5 rounded-lg text-white text-xs font-extrabold flex items-center gap-2 shadow-md"
                                     style={{ background: bg }}
                                     data-testid={`user-superset-${exIndex}`}>
                                  <span>🔗</span>
                                  <span>{label} {letter}</span>
                                  <span className="opacity-80 font-normal">· {size} ejercicios sin descanso</span>
                                </div>
                              );
                            })()}
                            <div
                              className="p-4 rounded-xl"
                              style={{
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderLeft: ((day.exercises || [])[exIndex]?.link_next || (exIndex > 0 && (day.exercises || [])[exIndex - 1]?.link_next))
                                  ? '4px solid #ec4899' : undefined,
                              }}
                            >
                            {/* Exercise Name */}
                            <div className="mb-3">
                              {/* Línea 1: nombre + equipo */}
                              <div className="mb-2">
                                <p className="font-bold text-white text-base leading-tight">
                                  {exercise.custom_name || exercise.name || exercise.exercise_name}
                                </p>
                                {exercise.equipment && exercise.equipment !== 'libre' && (
                                  <span className="text-xs text-cyan-400 flex items-center gap-1 mt-0.5">
                                    <FaDumbbell className="text-[10px]" />
                                    {EQUIPMENT_LABELS[exercise.equipment] || exercise.equipment}
                                  </span>
                                )}
                              </div>
                              {/* Línea 2: badges */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className="text-xs font-bold px-2 py-1 rounded"
                                  style={{ background: 'linear-gradient(135deg, #00F0FF, var(--brand-primary))', color: 'white' }}
                                >
                                  {exIndex + 1}
                                </span>
                                {/* Fit Evolution v2 · Badge por tipo de ejercicio */}
                                {exercise.exercise_type && (
                                  <span
                                    data-testid={`ex-type-${exIndex}`}
                                    className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide"
                                    style={
                                      exercise.exercise_type === 'activador'
                                        ? { background: 'rgba(251, 191, 36, 0.18)', color: '#FBBF24', border: '1px solid rgba(251, 191, 36, 0.35)' }
                                        : exercise.exercise_type === 'complementario'
                                        ? { background: 'rgba(139, 92, 246, 0.18)', color: '#A78BFA', border: '1px solid rgba(139, 92, 246, 0.35)' }
                                        : exercise.exercise_type === 'secundario'
                                        ? { background: 'rgba(16, 185, 129, 0.18)', color: '#34D399', border: '1px solid rgba(16, 185, 129, 0.35)' }
                                        : { background: 'rgba(var(--brand-primary-glow), 0.18)', color: 'var(--brand-primary-soft)', border: '1px solid rgba(var(--brand-primary-glow), 0.35)' }
                                    }
                                    title={`Ejercicio ${exercise.exercise_type}`}
                                  >
                                    {exercise.exercise_type === 'activador' ? '🔥 Activador' : exercise.exercise_type === 'complementario' ? '🎯 Complementario' : exercise.exercise_type === 'secundario' ? '➕ Secundario' : '⭐ Principal'}
                                  </span>
                                )}
                                {(exercise.video_url || exercise.video_id) ? (
                                  <button
                                    onClick={() => setVideoModal({ url: exercise.video_url, name: exercise.custom_name || exercise.name || exercise.exercise_name, source: exercise.video_source })}
                                    data-testid={`play-video-${exIndex}`}
                                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all hover:scale-105"
                                    style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))', color: 'white' }}
                                    title="Ver demostración del ejercicio"
                                  >
                                    <FaPlay className="text-[10px]" /> Video
                                  </button>
                                ) : (
                                  <span
                                    data-testid={`no-video-${exIndex}`}
                                    className="flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full"
                                    style={{ background: 'rgba(148, 163, 184, 0.15)', color: '#94A3B8', border: '1px solid rgba(148, 163, 184, 0.25)' }}
                                    title="Aún no tenemos video demostrativo para este ejercicio"
                                  >
                                    Sin video
                                  </span>
                                )}
                                {/* 🔁 Badge de alternativas definidas por admin */}
                                <AlternativeExerciseSwap
                                  exercise={exercise}
                                  exIndex={exIndex}
                                  dayIndex={dayIndex}
                                  onSwapped={fetchData}
                                />
                                {exercise.muscle_group && (
                                  <span className="text-xs text-gray-500 ml-auto">
                                    {exercise.muscle_group}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Badge de TÉCNICA de entrenamiento asignada · Fase 5 */}
                            {exercise.technique && exercise.technique.name && (
                              <div
                                data-testid={`ex-technique-${exIndex}`}
                                className="mb-3 rounded-lg px-3 py-2"
                                style={{ background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.35)' }}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-black uppercase tracking-wide" style={{ color: '#FBBF24' }}>🔥 Técnica</span>
                                  <span className="text-sm font-bold text-white">{exercise.technique.name}</span>
                                </div>
                                {exercise.technique.description && (
                                  <p className="text-xs text-gray-300 leading-snug">{exercise.technique.description}</p>
                                )}
                              </div>
                            )}
                            
                            {/* Información adicional del ejercicio (execution, objective, notes) */}
                            {(exercise.execution || exercise.objective || exercise.notes || exercise.section) && (
                              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                                {exercise.section && (
                                  <span 
                                    className="px-2 py-1 rounded-full font-semibold"
                                    style={{ background: 'rgba(255, 165, 0, 0.2)', color: '#FFA500' }}
                                  >
                                    {exercise.section}
                                  </span>
                                )}
                                {exercise.objective && (
                                  <span 
                                    className="px-2 py-1 rounded-full"
                                    style={{ background: 'rgba(0, 240, 255, 0.15)', color: '#00F0FF' }}
                                  >
                                    🎯 {exercise.objective}
                                  </span>
                                )}
                                {exercise.execution && (
                                  <span className="text-gray-400 flex-1">
                                    📝 {exercise.execution}
                                  </span>
                                )}
                                {exercise.notes && !exercise.execution && (
                                  <span className="text-gray-400 flex-1">
                                    📝 {exercise.notes}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Series Table */}
                            {series.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-gray-400 text-xs uppercase">
                                      <th className="text-left py-2 px-2">Serie</th>
                                      <th className="text-center py-2 px-2">Reps</th>
                                      <th className="text-center py-2 px-2">Descanso</th>
                                      <th className="text-left py-2 px-2">Método</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {series.map((s, sIndex) => {
                                      let methodDisplay = methodLabels[s.method] || s.method || 'Normal';
                                      
                                      // Add details for special methods
                                      if (s.method === 'tempo_controlado' && (s.tempo_eccentric || s.tempo_pause || s.tempo_concentric)) {
                                        methodDisplay += ` (${s.tempo_eccentric || '3'}-${s.tempo_pause || '1'}-${s.tempo_concentric || '1'})`;
                                      } else if (s.method === 'pausas_isometricas' && s.isometric_seconds) {
                                        const posLabels = { 'bottom': 'abajo', 'top': 'arriba', 'mid': 'medio' };
                                        methodDisplay += ` (${s.isometric_seconds}s${s.isometric_position ? ', ' + (posLabels[s.isometric_position] || s.isometric_position) : ''})`;
                                      } else if (s.method === 'excentrica_lenta' && s.eccentric_seconds) {
                                        methodDisplay += ` (${s.eccentric_seconds}s)`;
                                      }
                                      
                                      const isSpecialMethod = s.method && s.method !== 'normal';
                                      const reps = parseInt(s.reps) || 8;
                                      const exerciseName = exercise.custom_name || exercise.name || exercise.exercise_name || 'este ejercicio';
                                      
                                      // Generar instrucciones CONTEXTUALES adaptadas al ejercicio y repeticiones
                                      const getContextualInstructions = (method, reps, serie, exerciseName) => {
                                        const halfReps = Math.ceil(reps / 2);
                                        const thirdReps = Math.ceil(reps / 3);
                                        const methodLower = (method || '').toLowerCase().replace(/[-_\s]/g, '');
                                        
                                        // Rest-Pause
                                        if (methodLower.includes('restpause') || methodLower.includes('rest_pause') || method === 'rest_pause') {
                                          return `REST-PAUSE en ${exerciseName}:\nSerie: ${reps} reps al fallo\n↓ 10-15 segundos de pausa\nMini-serie: ${halfReps} reps más\n↓ 10-15 segundos\nMini-serie: ${thirdReps} reps más`;
                                        }
                                        
                                        // Dropset
                                        if (methodLower.includes('dropset') || methodLower.includes('drop') || method === 'dropset') {
                                          return `DROPSET en ${exerciseName}:\nSerie: ${reps} reps al fallo\n↓ Bajar peso 20-30%\n${halfReps} reps al fallo\n↓ Bajar peso 20-30%\n${thirdReps} reps al fallo`;
                                        }
                                        
                                        // Excéntrica Lenta
                                        if (methodLower.includes('excentrica') || methodLower.includes('eccentric') || method === 'excentrica_lenta') {
                                          const eccSeconds = serie.eccentric_seconds || 4;
                                          return `EXCÉNTRICA LENTA en ${exerciseName}:\n${reps} repeticiones con ${eccSeconds} segundos de bajada controlada\n\n💡 Enfócate en resistir el peso durante toda la fase de bajada`;
                                        }
                                        
                                        // Pausas Isométricas
                                        if (methodLower.includes('isometric') || methodLower.includes('pausa') || method === 'pausas_isometricas') {
                                          const isoSeconds = serie.isometric_seconds || 3;
                                          const posLabels = { 'bottom': 'en la posición baja', 'top': 'arriba', 'mid': 'a mitad del recorrido', 'medio': 'a mitad del recorrido' };
                                          const pos = posLabels[serie.isometric_position] || serie.isometric_position || 'en el punto de máxima tensión';
                                          return `PAUSAS ISOMÉTRICAS en ${exerciseName}:\n${reps} repeticiones con pausa de ${isoSeconds} segundos ${pos}\n\n💡 Mantén la contracción durante toda la pausa`;
                                        }
                                        
                                        // Tempo Controlado
                                        if (methodLower.includes('tempo') || method === 'tempo_controlado') {
                                          const ecc = serie.tempo_eccentric || 3;
                                          const pause = serie.tempo_pause || 1;
                                          const conc = serie.tempo_concentric || 1;
                                          return `TEMPO CONTROLADO en ${exerciseName}:\n${reps} repeticiones con tempo ${ecc}-${pause}-${conc}\n\n${ecc}s bajada → ${pause}s pausa → ${conc}s subida\n\n💡 Cuenta mentalmente en cada fase`;
                                        }
                                        
                                        // Super Lento
                                        if (methodLower.includes('superlento') || methodLower.includes('super_lento') || method === 'super_lento') {
                                          return `SUPER LENTO en ${exerciseName}:\n${reps} repeticiones con:\n5 segundos en la subida\n5 segundos en la bajada\n\n💡 Control total del movimiento, sin impulso`;
                                        }
                                        
                                        // Mecánico
                                        if (methodLower.includes('mecanico') || method === 'mecanico') {
                                          return `MECÁNICO en ${exerciseName}:\n${reps} reps en posición difícil\n↓ Sin descanso\n${reps} reps en posición más fácil\n\n💡 Cambiar el ángulo o agarre para continuar`;
                                        }
                                        
                                        // Myo-Reps
                                        if (methodLower.includes('myo') || method === 'myo_reps') {
                                          return `MYO-REPS en ${exerciseName}:\nSerie inicial: ${reps} reps al fallo\n↓ 5 seg descanso\n5 mini-reps\n↓ 5 seg descanso\n5 mini-reps\n(repetir hasta fallo total)`;
                                        }
                                        
                                        // Cluster
                                        if (methodLower.includes('cluster') || method === 'cluster') {
                                          return `CLUSTER en ${exerciseName}:\n${reps} reps totales divididas:\n${thirdReps} reps → 15 seg pausa\n${thirdReps} reps → 15 seg pausa\n${thirdReps} reps`;
                                        }
                                        
                                        // AMRAP
                                        if (methodLower.includes('amrap') || method === 'amrap') {
                                          return `AMRAP en ${exerciseName}:\nRealizar máximas repeticiones posibles\nObjetivo mínimo: ${reps} reps\n\n💡 Mantén buena técnica hasta el fallo`;
                                        }
                                        
                                        // EMOM
                                        if (methodLower.includes('emom') || method === 'emom') {
                                          return `EMOM en ${exerciseName}:\nCompletar ${reps} reps al inicio de cada minuto\nDescansar el tiempo restante del minuto`;
                                        }
                                        
                                        // Piramidal
                                        if (methodLower.includes('piramid') || method === 'piramidal') {
                                          return `PIRAMIDAL en ${exerciseName}:\n${reps} reps con peso progresivo\n\n💡 Aumentar peso gradualmente según la pirámide`;
                                        }
                                        
                                        // Fallo Técnico
                                        if (methodLower.includes('fallo') || method === 'fallo_tecnico') {
                                          return `FALLO TÉCNICO en ${exerciseName}:\nRealizar repeticiones hasta que la técnica se deteriore\nObjetivo: ${reps} reps o más\n\n💡 Detente cuando pierdas el control del movimiento`;
                                        }
                                        
                                        // Pre-Fatiga
                                        if (methodLower.includes('prefatiga') || method === 'pre_fatiga') {
                                          return `PRE-FATIGA en ${exerciseName}:\n${reps} reps después del ejercicio de aislamiento\n\n💡 El músculo ya está pre-fatigado`;
                                        }
                                        
                                        // Post-Fatiga
                                        if (methodLower.includes('postfatiga') || method === 'post_fatiga') {
                                          return `POST-FATIGA en ${exerciseName}:\n${reps} reps de aislamiento inmediatamente después\n\n💡 Quemar el músculo después del compuesto`;
                                        }
                                        
                                        // Negativas
                                        if (methodLower.includes('negativ')) {
                                          return `NEGATIVAS en ${exerciseName}:\n${reps} reps enfocando en la bajada\n4-6 segundos de fase excéntrica controlada`;
                                        }
                                        
                                        // Back-off Set
                                        if (methodLower.includes('backoff') || methodLower.includes('back_off') || method === 'back_off_set' || method === 'back_off') {
                                          return `BACK-OFF SET en ${exerciseName}:\nSeries principales: ${reps} reps (peso pesado)\n↓ Después de las series pesadas\nSerie final: -30% peso → Reps hasta el fallo\n\n💡 La serie de back-off añade volumen efectivo con menos fatiga del SNC.`;
                                        }
                                        
                                        // Método no reconocido - NUNCA mostrar texto genérico, dejar null para que use custom_method_notes
                                        return null;
                                      };
                                      
                                      // SIEMPRE mostrar custom_method_notes si existe - el admin sabe lo que escribe
                                      let instructions = null;
                                      if (isSpecialMethod) {
                                        if (s.custom_method_notes && s.custom_method_notes.trim()) {
                                          // Mostrar EXACTAMENTE lo que el admin escribió
                                          instructions = s.custom_method_notes;
                                        } else {
                                          // Solo si no hay notas personalizadas, generar las instrucciones
                                          instructions = getContextualInstructions(s.method, reps, s, exerciseName);
                                        }
                                      }
                                      
                                      return (
                                        <React.Fragment key={sIndex}>
                                          <tr className="border-t border-white/5">
                                            <td className="py-2 px-2 text-white font-medium">{sIndex + 1}</td>
                                            <td className="py-2 px-2 text-center text-white font-bold">{s.reps || '-'}</td>
                                            <td className="py-2 px-2 text-center text-gray-400">
                                              {s.custom_rest_seconds 
                                                ? `${s.custom_rest_seconds}s` 
                                                : (restLabels[s.rest] || (s.rest && s.rest !== 'custom' ? `${s.rest}s` : '-'))}
                                            </td>
                                            <td className="py-2 px-2">
                                              <span 
                                                className={`text-xs px-2 py-1 rounded-full ${isSpecialMethod ? 'font-medium' : ''}`}
                                                style={isSpecialMethod ? { 
                                                  background: 'rgba(var(--brand-primary-glow), 0.2)', 
                                                  color: 'var(--brand-primary)' 
                                                } : { color: '#9CA3AF' }}
                                              >
                                                {methodDisplay}
                                              </span>
                                            </td>
                                          </tr>
                                          {/* Instrucciones del método - aparecen DEBAJO de cada fila con método especial */}
                                          {instructions && (
                                            <tr className="border-0">
                                              <td colSpan={4} className="py-1 px-2">
                                                <div 
                                                  className="text-xs p-3 rounded-lg ml-4 whitespace-pre-wrap"
                                                  style={{ 
                                                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(var(--brand-primary-glow), 0.05))',
                                                    borderLeft: '3px solid var(--brand-primary)'
                                                  }}
                                                >
                                                  <span className="text-gray-200">{instructions}</span>
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              /* Fallback for legacy format */
                              <div className="flex gap-4 text-sm text-gray-400">
                                {exercise.sets && <span>{exercise.sets} series</span>}
                                {exercise.reps && <span>{exercise.reps} reps</span>}
                                {(exercise.rest || exercise.rest_time) && (
                                  <span>Descanso: {exercise.rest || exercise.rest_time}</span>
                                )}
                              </div>
                            )}
                            
                            {/* Exercise Notes */}
                            {exercise.notes && (
                              <p 
                                className="text-xs mt-3 italic px-2 py-2 rounded"
                                style={{ background: 'rgba(0, 240, 255, 0.1)', color: '#00F0FF' }}
                              >
                                💡 {exercise.notes}
                              </p>
                            )}
                          </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">
                        Ejercicios pendientes de agregar...
                      </p>
                    )}
                  </div>
                  
                  {/* Day Notes */}
                  {day.day_notes && (
                    <div 
                      className="mt-4 p-3 rounded-lg text-sm"
                      style={{ background: 'rgba(var(--brand-primary-glow), 0.1)', color: 'var(--brand-primary)' }}
                    >
                      📝 {day.day_notes}
                    </div>
                  )}
                </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div 
            className="rounded-2xl p-12 text-center backdrop-blur-xl"
            style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <FaDumbbell className="text-5xl mx-auto mb-4" style={{ color: '#00F0FF' }} />
            <h3 className="text-xl font-semibold text-white mb-2">Tu rutina está siendo preparada</h3>
            <p className="text-gray-400">Pronto tendrás tu plan de entrenamiento completo aquí</p>
          </div>
        )}

        {/* Notes */}
        {(workoutPlan.notes || workoutPlan.workout_notes) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8"
          >
            <div 
              className="rounded-2xl p-6 backdrop-blur-xl"
              style={{ 
                background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(var(--brand-primary-glow), 0.1))',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <FaFire style={{ color: 'var(--brand-primary)' }} /> Notas Importantes
              </h4>
              <p className="text-gray-300 whitespace-pre-wrap">{workoutPlan.notes || workoutPlan.workout_notes}</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Video Modal - Ejercicio */}
      {videoModal && (
        <div
          data-testid="exercise-video-modal"
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
          style={{ background: 'rgba(0,0,0,0.9)' }}
          onClick={() => setVideoModal(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-4xl rounded-3xl overflow-hidden"
            style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-bold text-lg truncate flex-1 pr-3">{videoModal.name}</h3>
              <button
                onClick={() => setVideoModal(null)}
                data-testid="close-exercise-video"
                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white"
              >
                <FaTimes />
              </button>
            </div>
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <VideoPlayer
                url={videoModal.url}
                source={videoModal.source}
                title={videoModal.name}
              />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MyWorkout;
